// src/pages/DeliveryNotes/DnEditPrint.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import {
  getSalesOrder,
  getDNItemsByDNID,
  getDNHeaderByDNID,
  listDNPrintEdits,
  createDNPrintEdit,
  getDNPrintEdit
} from "../../services/apiService";

import DnPrintTemplate from "./DnPrintTemplate";
import "./DnEditPrint.css";

const normalizeDateOnly = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const normalizeText = (value) => {
  return String(value || "").trim().toUpperCase();
};

// ✅ Merge same Catalog + Lot + Expiry into one row
const mergeSameDNItems = (items = []) => {
  const map = new Map();

  items.forEach((it) => {
    const CatalogNumber = it.CatalogNumber || "";
    const Description = it.Description || "";
    const Packing = it.Packing || "";
    const LotNumber = it.LotNumber || "";
    const ExpiryDate = normalizeDateOnly(it.ExpiryDate);
    const QtyDelivered = Number(it.QtyDelivered || 0);

    const key = [
      normalizeText(CatalogNumber),
      normalizeText(LotNumber),
      ExpiryDate
    ].join("|");

    if (!map.has(key)) {
      map.set(key, {
        DNItemID: it.DNItemID ?? null,
        ItemID: it.ItemID ?? null,
        CatalogNumber,
        Description,
        Packing,
        LotNumber,
        ExpiryDate,
        QtyDelivered
      });
    } else {
      const existing = map.get(key);

      existing.QtyDelivered =
        Number(existing.QtyDelivered || 0) + QtyDelivered;

      existing.Description = existing.Description || Description;
      existing.Packing = existing.Packing || Packing;
      existing.ItemID = existing.ItemID || it.ItemID || null;
      existing.DNItemID = existing.DNItemID || it.DNItemID || null;
    }
  });

  return Array.from(map.values());
};

export default function DnEditPrint() {
  const { dnid } = useParams();
  const DNID = useMemo(() => Number(dnid), [dnid]);

  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);

  // must be passed from DeliveryNotes.jsx
  const SOID = location?.state?.SOID ? Number(location.state.SOID) : null;

  const [loading, setLoading] = useState(true);

  // original sources
  const [dnOriginal, setDnOriginal] = useState(null);
  const [so, setSo] = useState(null);
  const [itemsOriginal, setItemsOriginal] = useState([]);

  // edit history
  const [editHistory, setEditHistory] = useState([]);
  const [selectedEditID, setSelectedEditID] = useState("");

  // edit form header
  const [editHeader, setEditHeader] = useState({
    TemplateCode: "STANDARD",
    OrderType: "NORMAL",
    Reason: "",
    CustomerName: "",
    ShipTo: "",
    MarkTo: "",
    ReferenceOrder: "",
    DNDate: "",
    DNNumber: "",
    SpecialInstruction: ""
  });

  // edit snapshot items
  const [saveItemSnapshot, setSaveItemSnapshot] = useState(true);
  const [editItems, setEditItems] = useState([]);

  // preview
  const [mode, setMode] = useState("EDITED"); // ORIGINAL | EDITED
  const [showPreview, setShowPreview] = useState(false);

  // ✅ FIX: copy label state for DN print template
  const [copyType, setCopyType] = useState("CUSTOMER'S COPY");

  const getOverrideValue = (hdr, key) => {
    if (!hdr) return "";

    if (hdr.Overrides && typeof hdr.Overrides === "object") {
      return hdr.Overrides?.[key] || "";
    }

    if (hdr.Overrides && typeof hdr.Overrides === "string") {
      try {
        const parsed = JSON.parse(hdr.Overrides);
        return parsed?.[key] || "";
      } catch {
        return "";
      }
    }

    return "";
  };

  const loadAll = async () => {
    setLoading(true);

    try {
      if (!Number.isFinite(DNID) || DNID <= 0) {
        alert("Invalid DNID.");
        navigate("/delivery-notes");
        return;
      }

      if (!Number.isFinite(SOID) || SOID <= 0) {
        alert("Missing SO reference. Please open this from Delivery Notes history.");
        navigate("/delivery-notes");
        return;
      }

      // 1. Load SO
      const soData = await getSalesOrder(SOID);
      setSo(soData);

      // 2. Load DN header
      const dnHdr = await getDNHeaderByDNID(DNID, SOID);
      if (!dnHdr) {
        alert("DN header not found under this SO.");
        navigate("/delivery-notes");
        return;
      }

      setDnOriginal(dnHdr);

      // 3. Load original DN items
      const dnItems = await getDNItemsByDNID(DNID);
      setItemsOriginal(dnItems || []);

      // 4. Load edit history
      const hist = await listDNPrintEdits(DNID);
      setEditHistory(hist || []);

      // 5. Init edit header values from original
      setEditHeader({
        TemplateCode: "STANDARD",
        OrderType: soData?.OrderType || soData?.ordertype || "NORMAL",
        Reason: "",
        CustomerName:
          soData?.CustomerName ||
          soData?.customername ||
          soData?.Customer_Name ||
          "",
        ShipTo: soData?.ShipTo || soData?.shipto || "",
        MarkTo: soData?.MarkTo || soData?.markto || "",

        ReferenceOrder:
          soData?.ReferenceOrder ||
          soData?.reference_order ||
          soData?.ReferenceNo ||
          soData?.ReferenceOrderNo ||
          "",

        DNDate: dnHdr?.DNDate ? String(dnHdr.DNDate).slice(0, 10) : "",
        DNNumber: dnHdr?.DNNumber || "",
        SpecialInstruction:
          dnHdr?.SpecialInstruction ||
          dnHdr?.special_instruction ||
          ""
      });

      // 6. Init editable items snapshot
      setSaveItemSnapshot(true);
      setEditItems(
        mergeSameDNItems(
          (dnItems || []).map((it) => ({
            DNItemID: it.DNItemID ?? null,
            ItemID: it.ItemID ?? null,
            CatalogNumber: it.CatalogNumber || "",
            Description: it.Description || "",
            Packing: it.Packing || "",
            LotNumber: it.LotNumber || "",
            ExpiryDate: normalizeDateOnly(it.ExpiryDate),
            QtyDelivered: Number(it.QtyDelivered || 0)
          }))
        )
      );

      setSelectedEditID("");
      setCopyType("CUSTOMER'S COPY");
    } catch (e) {
      console.error(e);
      alert("Failed to load DN Edit page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DNID]);

  // load existing edit version
  const handleSelectEdit = async (editid) => {
    if (!editid) {
      setSelectedEditID("");
      return;
    }

    try {
      const data = await getDNPrintEdit(editid);
      const hdr = data?.header;
      const items = data?.items || [];

      if (!hdr) {
        alert("Edit not found.");
        return;
      }

      setSelectedEditID(String(editid));

      setEditHeader((p) => ({
        ...p,
        TemplateCode: hdr.TemplateCode || "STANDARD",
        OrderType: hdr.OrderType || p.OrderType,
        Reason: hdr.Reason || "",

        CustomerName:
          hdr.CustomerName ||
          hdr.customername ||
          getOverrideValue(hdr, "CustomerName") ||
          "",

        ShipTo:
          hdr.ShipTo ||
          hdr.shipto ||
          getOverrideValue(hdr, "ShipTo") ||
          "",

        MarkTo:
          hdr.MarkTo ||
          hdr.markto ||
          getOverrideValue(hdr, "MarkTo") ||
          "",

        ReferenceOrder:
          hdr.ReferenceOrder ||
          hdr.reference_order ||
          hdr.ReferenceNo ||
          hdr.ReferenceOrderNo ||
          getOverrideValue(hdr, "ReferenceOrder") ||
          p.ReferenceOrder ||
          "",

        DNDate:
          hdr.DNDate
            ? String(hdr.DNDate).slice(0, 10)
            : getOverrideValue(hdr, "DNDate")
              ? String(getOverrideValue(hdr, "DNDate")).slice(0, 10)
              : "",

        DNNumber:
          hdr.DNNumber ||
          getOverrideValue(hdr, "DNNumber") ||
          "",

        SpecialInstruction:
          hdr.SpecialInstruction ||
          hdr.special_instruction ||
          getOverrideValue(hdr, "SpecialInstruction") ||
          ""
      }));

      // ✅ if saved items exist, use them, but merge duplicate rows again
      if (items.length > 0) {
        setSaveItemSnapshot(true);
        setEditItems(
          mergeSameDNItems(
            items.map((it) => ({
              DNItemID: it.DNItemID ?? null,
              ItemID: it.ItemID ?? null,
              CatalogNumber: it.CatalogNumber || "",
              Description: it.Description || "",
              Packing: it.Packing || "",
              LotNumber: it.LotNumber || "",
              ExpiryDate: normalizeDateOnly(it.ExpiryDate),
              QtyDelivered: Number(it.QtyDelivered || 0)
            }))
          )
        );
      } else {
        setSaveItemSnapshot(false);
      }

      setCopyType("CUSTOMER'S COPY");
    } catch (e) {
      console.error(e);
      alert("Failed to load edit version.");
    }
  };

  // save a new edit version
  const saveEditVersion = async () => {
    try {
      // ✅ merge one more time before saving, just in case user edited duplicate keys
      const mergedItemsBeforeSave = mergeSameDNItems(editItems);

      const payload = {
        TemplateCode: editHeader.TemplateCode || "STANDARD",
        OrderType: editHeader.OrderType || null,
        Reason: editHeader.Reason || null,
        EditedBy: null,

        Overrides: {
          CustomerName: editHeader.CustomerName || null,
          ShipTo: editHeader.ShipTo || null,
          MarkTo: editHeader.MarkTo || null,
          ReferenceOrder: editHeader.ReferenceOrder || null,
          DNDate: editHeader.DNDate === "" ? null : editHeader.DNDate,
          DNNumber: editHeader.DNNumber || null,
          SpecialInstruction: editHeader.SpecialInstruction || null
        },

        Items: saveItemSnapshot
          ? mergedItemsBeforeSave.map((it) => ({
              DNItemID: it.DNItemID ?? null,
              ItemID: it.ItemID ?? null,
              CatalogNumber: it.CatalogNumber ?? null,
              Description: it.Description ?? null,
              Packing: it.Packing ?? null,
              LotNumber: it.LotNumber ?? null,
              ExpiryDate: it.ExpiryDate || null,
              QtyDelivered: it.QtyDelivered ?? null
            }))
          : []
      };

      const res = await createDNPrintEdit(DNID, payload);

      alert(`✅ Saved print edit. EditID: ${res.EditID}`);

      // refresh history
      const hist = await listDNPrintEdits(DNID);
      setEditHistory(hist || []);
      setSelectedEditID(String(res.EditID));

      // keep UI merged after saving
      setEditItems(mergedItemsBeforeSave);
    } catch (e) {
      console.error(e);
      alert("Failed to save print edit.");
    }
  };

  // build effective print SO object
  const effectiveSO = useMemo(() => {
    if (!so) return null;

    return {
      ...so,

      SONumber:
        so.SONumber ||
        so.SO_Number ||
        so.so_number ||
        so.SONo ||
        so.SO_No ||
        "",

      OrderType: editHeader.OrderType || so.OrderType,

      CustomerName:
        editHeader.CustomerName ||
        so.CustomerName ||
        so.customername ||
        so.Customer_Name ||
        "",

      ShipTo:
        editHeader.ShipTo ||
        so.ShipTo ||
        so.shipto ||
        "",

      MarkTo:
        editHeader.MarkTo ||
        so.MarkTo ||
        so.markto ||
        "",

      ReferenceOrder:
        editHeader.ReferenceOrder ||
        so.ReferenceOrder ||
        so.reference_order ||
        so.ReferenceNo ||
        so.ReferenceOrderNo ||
        ""
    };
  }, [so, editHeader]);

  // build effective print DN object
  const effectiveDN = useMemo(() => {
    if (!dnOriginal) return null;

    const dnDate =
      editHeader.DNDate === ""
        ? ""
        : editHeader.DNDate || dnOriginal.DNDate;

    return {
      ...dnOriginal,

      DNDate: dnDate,
      DNNumber: editHeader.DNNumber || dnOriginal.DNNumber,

      SpecialInstruction:
        editHeader.SpecialInstruction ||
        dnOriginal.SpecialInstruction ||
        dnOriginal.special_instruction ||
        "",

      special_instruction:
        editHeader.SpecialInstruction ||
        dnOriginal.SpecialInstruction ||
        dnOriginal.special_instruction ||
        ""
    };
  }, [dnOriginal, editHeader]);

  const previewDN = mode === "ORIGINAL" ? dnOriginal : effectiveDN;
  const previewSO = mode === "ORIGINAL" ? so : effectiveSO;

  const SONumber =
    previewSO?.SONumber ||
    previewSO?.SO_Number ||
    previewSO?.so_number ||
    previewSO?.SONo ||
    previewSO?.SO_No ||
    previewSO?.SOID ||
    SOID ||
    "";

  const previewItems =
    mode === "ORIGINAL"
      ? itemsOriginal
      : saveItemSnapshot
        ? mergeSameDNItems(editItems)
        : itemsOriginal;

  // ✅ FIXED: 4 copies and uses React copyType state instead of window event
  const download4Copies = async () => {
    if (!printRef.current) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const copyTypes = [
      "CUSTOMER'S COPY",
      "RECEIVING COPY",
      "WAREHOUSE COPY",
      "SALES COPY",
    ];

    let pdfPageCount = 0;

    try {
      for (const currentCopyType of copyTypes) {
        setCopyType(currentCopyType);

        // Wait until React has rendered the new copy label.
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });

        // Wait for web fonts before taking the screenshot, when supported.
        if (document.fonts?.ready) {
          await document.fonts.ready;
        }

        const printPages = Array.from(
          printRef.current.querySelectorAll(".dn-print-page")
        );

        if (!printPages.length) {
          throw new Error("No Delivery Note print pages were found.");
        }

        for (const printPage of printPages) {
          const canvas = await html2canvas(printPage, {
            scale: 3,
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#ffffff",
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: Math.max(1200, window.innerWidth),
            onclone: (_document, clonedPage) => {
              // html2canvas otherwise clips the sheet to scrollable modal ancestors.
              let ancestor = clonedPage.parentElement;
              while (ancestor) {
                ancestor.style.setProperty("overflow", "visible", "important");
                ancestor.style.setProperty("max-height", "none", "important");
                ancestor = ancestor.parentElement;
              }
            },
          });

          const imgData = canvas.toDataURL("image/png");

          // Fit exactly one captured .dn-print-page on one A4 PDF page.
          const canvasRatio = canvas.width / canvas.height;
          const a4Ratio = pageWidth / pageHeight;

          let imgWidth;
          let imgHeight;

          if (canvasRatio > a4Ratio) {
            imgWidth = pageWidth;
            imgHeight = pageWidth / canvasRatio;
          } else {
            imgHeight = pageHeight;
            imgWidth = pageHeight * canvasRatio;
          }

          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;

          if (pdfPageCount > 0) {
            doc.addPage("a4", "portrait");
          }

          doc.addImage(
            imgData,
            "PNG",
            x,
            y,
            imgWidth,
            imgHeight,
            undefined,
            "FAST"
          );

          pdfPageCount += 1;
        }
      }

      const dnNo = previewDN?.DNNumber || "DN";
      doc.save(`${dnNo}_${mode}_4_COPIES.pdf`);
    } catch (error) {
      console.error("download4Copies ERROR:", error);
      alert(error?.message || "Failed to generate the Delivery Note PDF.");
    } finally {
      setCopyType("CUSTOMER'S COPY");
    }
  };

  if (loading) {
    return <div style={{ padding: 16 }}>Loading...</div>;
  }

  return (
    <div className="dnedit-container">
      <div className="dnedit-header">
        <h2 style={{ margin: 0 }}>DN Edit (Print Override)</h2>

        <button className="cancel-btn" onClick={() => navigate(-1)}>
          ⬅ Back
        </button>
      </div>

      {!dnOriginal || !so ? (
        <div style={{ padding: 12 }}>Missing DN/SO data.</div>
      ) : (
        <>
          <div className="dnedit-topbar">
            <div>
              <strong>DN:</strong> {dnOriginal.DNNumber} &nbsp; | &nbsp;
              <strong>SO NUMBER:</strong> {SONumber}
            </div>

            <div className="dnedit-mode">
              <label>Preview:</label>

              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="EDITED">Edited</option>
                <option value="ORIGINAL">Original</option>
              </select>

              <button
                className="print-btn"
                onClick={() => {
                  setCopyType("CUSTOMER'S COPY");
                  setShowPreview(true);
                }}
              >
                View / Print
              </button>
            </div>
          </div>

          <div className="dnedit-grid">
            {/* LEFT PANEL */}
            <div className="dnedit-panel">
              <h3>Override Header</h3>

              <div className="dnedit-row">
                <label>Saved Versions</label>

                <select
                  value={selectedEditID}
                  onChange={(e) => handleSelectEdit(e.target.value)}
                >
                  <option value="">-- Select saved edit --</option>

                  {editHistory.map((h) => (
                    <option key={h.EditID} value={h.EditID}>
                      #{h.EditID} • {h.EditedAt} • {h.Reason || "No reason"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dnedit-row">
                <label>Order Type</label>

                <select
                  value={editHeader.OrderType}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      OrderType: e.target.value
                    }))
                  }
                >
                  <option value="NORMAL">NORMAL</option>
                  <option value="DEMO">DEMO</option>
                  <option value="FREE_GOODS">FREE_GOODS</option>
                  <option value="BIOCARE_ACCOUNT">BIOCARE_ACCOUNT</option>
                  <option value="REPLACEMENT">REPLACEMENT</option>
                  <option value="AS_TESTING">AS_TESTING</option>
                  <option value="TSD_TESTING">TSD_TESTING</option>
                  <option value="ADVANCE_DELIVERY">ADVANCE_DELIVERY</option>
                </select>
              </div>

              <div className="dnedit-row">
                <label>Customer</label>

                <input
                  value={editHeader.CustomerName}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      CustomerName: e.target.value
                    }))
                  }
                />
              </div>

              <div className="dnedit-row">
                <label>Ship To</label>

                <input
                  value={editHeader.ShipTo}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      ShipTo: e.target.value
                    }))
                  }
                />
              </div>

              <div className="dnedit-row">
                <label>Mark To</label>

                <input
                  value={editHeader.MarkTo}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      MarkTo: e.target.value
                    }))
                  }
                />
              </div>

              <div className="dnedit-row">
                <label>Reference Order</label>

                <input
                  value={editHeader.ReferenceOrder}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      ReferenceOrder: e.target.value
                    }))
                  }
                  placeholder="Enter reference order"
                />
              </div>

              <div className="dnedit-row">
                <label>DN Date</label>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="date"
                    value={editHeader.DNDate || ""}
                    onChange={(e) =>
                      setEditHeader((p) => ({
                        ...p,
                        DNDate: e.target.value
                      }))
                    }
                  />

                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() =>
                      setEditHeader((p) => ({
                        ...p,
                        DNDate: ""
                      }))
                    }
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="dnedit-row">
                <label>DN Number</label>

                <input
                  value={editHeader.DNNumber}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      DNNumber: e.target.value
                    }))
                  }
                />
              </div>

              <div className="dnedit-row">
                <label>Special Instruction</label>

                <textarea
                  rows={3}
                  value={editHeader.SpecialInstruction}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      SpecialInstruction: e.target.value
                    }))
                  }
                />
              </div>

              <div className="dnedit-row">
                <label>Reason (record)</label>

                <input
                  value={editHeader.Reason}
                  onChange={(e) =>
                    setEditHeader((p) => ({
                      ...p,
                      Reason: e.target.value
                    }))
                  }
                  placeholder="e.g. corrected reference order for print copy"
                />
              </div>

              <div className="dnedit-row inline-row">
                <label>Save item snapshot?</label>

                <input
                  type="checkbox"
                  checked={saveItemSnapshot}
                  onChange={(e) => setSaveItemSnapshot(e.target.checked)}
                />

                <small>(recommended for exact reprint)</small>
              </div>

              <div className="dnedit-actions">
                <button className="confirm-btn" onClick={saveEditVersion}>
                  SAVE EDIT VERSION
                </button>

                <button className="cancel-btn" onClick={loadAll}>
                  Reload Original
                </button>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="dnedit-panel">
              <h3>Items (Print Snapshot)</h3>

              {!saveItemSnapshot ? (
                <div style={{ padding: 10, opacity: 0.8 }}>
                  Snapshot disabled — edited print will use ORIGINAL items.
                </div>
              ) : (
                <table className="dnedit-table">
                  <thead>
                    <tr>
                      <th>Catalog</th>
                      <th>Description</th>
                      <th>Packing</th>
                      <th>Lot</th>
                      <th>Expiry</th>
                      <th>Qty</th>
                    </tr>
                  </thead>

                  <tbody>
                    {editItems.map((it, idx) => (
                      <tr
                        key={`${it.CatalogNumber}-${it.LotNumber}-${it.ExpiryDate}-${idx}`}
                      >
                        <td>
                          <input
                            value={it.CatalogNumber}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, CatalogNumber: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>

                        <td>
                          <input
                            value={it.Description}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, Description: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>

                        <td>
                          <input
                            value={it.Packing}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, Packing: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>

                        <td>
                          <input
                            value={it.LotNumber}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, LotNumber: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>

                        <td>
                          <input
                            type="date"
                            value={it.ExpiryDate || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, ExpiryDate: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>

                        <td style={{ width: 90 }}>
                          <input
                            type="number"
                            step="1"
                            value={it.QtyDelivered}
                            onChange={(e) => {
                              const v = Number(e.target.value || 0);
                              setEditItems((p) =>
                                p.map((x, i) =>
                                  i === idx
                                    ? { ...x, QtyDelivered: v }
                                    : x
                                )
                              );
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* PREVIEW MODAL */}
          {showPreview && (
            <div className="modal-overlay">
              <div className="modal-content dn-preview-modal">
                <div ref={printRef}>
                  <DnPrintTemplate
                    dn={previewDN}
                    so={previewSO}
                    items={previewItems}
                    mode={mode}
                    copyType={copyType}
                    showOverrideNote={false}
                  />
                </div>

                <div className="modal-actions">
                  <button className="confirm-btn" onClick={download4Copies}>
                    DOWNLOAD 4 COPIES PDF
                  </button>

                  <button
                    className="cancel-btn"
                    onClick={() => setShowPreview(false)}
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}